import { HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { find, flatten, head, isEmpty } from 'lodash-es';
import { from, of, throwError, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  delayWhen,
  exhaustMap,
  filter,
  map,
  mergeMap,
  retry,
  switchMap,
  tap,
  toArray,
} from 'rxjs/operators';

import { AUTH_ERROR_CODE, MESSAGE, SYSTEM_MAINTENANCE, constants } from '@shared/constants';
import { SKIP_ERROR_NOTIFICATION, SKIP_SESSION_LOGOUT } from '@shared/interceptors/http-context-tokens';
import { LinkedBusinessAccount } from '@shared/models';
import {
  AuthService,
  BusinessAccountService,
  CustomerService,
  EarnedWageService,
  FinancialAccountService,
  IdentityVerificationService,
  MocketBankService,
  StorageService,
  UserService,
} from '@shared/services';
import { TransactionService } from '@shared/services/transaction/transaction.service';
import { AuthActions, LoginPageActions, MessagesActions, ModalsActions, fromAuth, fromRoot } from '@shared/store';

// Retries for the linked business accounts lookup at login, with a 1s, then 2s backoff
export const LINKED_BUSINESS_ACCOUNTS_RETRIES = 2;

@Injectable()
export class AuthEffects implements OnInitEffects {
  storageService = inject(StorageService);

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private userService: UserService,
    private financialAccountService: FinancialAccountService,
    private earnedWageService: EarnedWageService,
    private businessAccountService: BusinessAccountService,
    private identityVerificationService: IdentityVerificationService,
    private customerService: CustomerService,
    private mocketbnkService: MocketBankService,
    private transactionService: TransactionService,
    private router: Router,
    private store: Store,
  ) {}

  ngrxOnInitEffects() {
    return AuthActions.rehydrateState();
  }

  pageReload() {
    location.reload();
  }

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LoginPageActions.login),
      map((action) => action.userCredentials),
      exhaustMap(({ email, password, tenant }) =>
        this.authService
          .getAuthTenantInfo(tenant)
          .pipe(
            catchError((error) => {
              if (error === SYSTEM_MAINTENANCE) {
                return throwError(() => error);
              }
              return throwError(() => new Error(MESSAGE.AUTH_PROVIDER_VERIFICATION_ERROR));
            }),
          )
          .pipe(
            exhaustMap(({ providerTenantId, authProvider, special }) => {
              this.store.dispatch(AuthActions.setSponsorAuthTenant({ isSponsorTenant: special ?? false }));

              if (authProvider == 'FIREBASE' || authProvider == 'GCP_IDENTITY_PLATFORM') {
                return this.authService
                  .googleCloudLogin(email, password, providerTenantId)
                  .pipe(catchError((error) => throwError(() => new Error(error.message))));
              } else {
                return throwError(() => new Error(`Authentication provider ${authProvider} is not supported`));
              }
            }),
            exhaustMap((accessToken) =>
              this.authService.exchangeToken(accessToken, tenant).pipe(
                catchError((error) => {
                  // Login blocked by an expired password: carry the fresh provider token so the user
                  // can set a new one without signing in again. The interceptor passes the body through.
                  if (error?.errorCode === AUTH_ERROR_CODE.passwordExpired) {
                    return throwError(() => ({ passwordExpired: true, providerToken: accessToken, email }));
                  }
                  return throwError(() => new Error(MESSAGE.UNKNOWN_ERROR));
                }),
              ),
            ),
            exhaustMap(() => this.userService.getUserDetails().pipe(catchError(() => throwError(() => new Error(MESSAGE.UNKNOWN_ERROR))))),
            switchMap((user) => {
              // The login handles a failed lookup itself: no "session expired" toast and no logout per attempt,
              // which would end the session a successful retry still needs
              const loginLookupContext = new HttpContext().set(SKIP_ERROR_NOTIFICATION, true).set(SKIP_SESSION_LOGOUT, true);

              return this.businessAccountService
                .getLinkedBusinessAccounts({ page: 0, size: constants.DEFAULT_PAGE_SIZE }, loginLookupContext)
                .pipe(
                  map((linkedBusinessAccounts) => {
                    const businessAccount = find(linkedBusinessAccounts.content, ['parent', true]) ?? head(linkedBusinessAccounts.content);

                    return { user, businessAccount: businessAccount ?? null };
                  }),
                  // A failed lookup must fail the login. Continuing with no business account sends every
                  // BA-scoped request to `business-accounts/undefined`. An empty list is still allowed: sponsor
                  // tenant users pick their business account after login.
                  retry({ count: LINKED_BUSINESS_ACCOUNTS_RETRIES, delay: (_error, attempt) => timer(1000 * attempt) }),
                  catchError(() => throwError(() => new Error(MESSAGE.UNKNOWN_ERROR))),
                );
            }),
            switchMap(({ user, businessAccount }) => {
              if (!businessAccount) {
                return of({ user, businessAccount });
              }

              return this.userService.getUserFullDetails({ businessAccountId: businessAccount.id, userId: user.id }).pipe(
                map((userDetailedResponse) => {
                  // IRIS/watcher employees (e.g. kor_employee) come back without a `roles` array;
                  // leave role undefined so selectUserRole falls back to 'View Only'.
                  return { user: { ...user, role: userDetailedResponse.roles?.[0]?.name }, businessAccount };
                }),
              );
            }),
            map(({ user, businessAccount }) => {
              return AuthActions.loginSuccess({ user, businessAccount, tenant });
            }),
            catchError((error) => {
              if (error === SYSTEM_MAINTENANCE) {
                return of(LoginPageActions.loggedInRedirectToMaintenance({ tenant }));
              }
              if (error?.passwordExpired) {
                return of(LoginPageActions.passwordExpired({ providerToken: error.providerToken, email: error.email, tenant }));
              }
              return of(AuthActions.loginFailure({ error: error.message }));
            }),
          ),
      ),
    );
  });

  passwordExpiredRedirect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(LoginPageActions.passwordExpired),
        tap(({ providerToken, email, tenant }) => {
          // Pass the fresh provider token via in-memory router state only — never persisted.
          this.router.navigate(['set-new-password'], { state: { providerToken, email, tenant } });
        }),
      );
    },
    { dispatch: false },
  );

  businessAccountDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.refreshTokenSuccess),
      concatLatestFrom(() => [
        this.store.select(fromAuth.selectBusinessAccount),
        this.store.select(fromAuth.selectBusinessAccountDetails),
        this.store.select(fromAuth.selectSponsorTenant),
      ]),
      filter(([, , businessAccountDetails, isSpecialTenant]) => !(businessAccountDetails && !isSpecialTenant)),
      exhaustMap(([, businessAccount]) => {
        if (!businessAccount) {
          return of(AuthActions.businessAccountDetails({ businessAccountDetails: null }));
        }

        return this.businessAccountService.getBusinessAccount(businessAccount.id).pipe(
          map((response) => AuthActions.businessAccountDetails({ businessAccountDetails: response })),
          catchError(() => of(AuthActions.businessAccountDetails({ businessAccountDetails: null }))),
        );
      }),
    );
  });

  rehydrateBusinessAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.rehydrateState),
      concatLatestFrom(() => [this.store.select(fromAuth.selectBusinessAccountDetails), this.store.select(fromAuth.selectSponsorTenant)]),
      exhaustMap(([, businessAccount]) => {
        if (!businessAccount) {
          return of();
        }
        return this.businessAccountService.getBusinessAccount(businessAccount.id).pipe(
          map((response) => AuthActions.rehydrateBusinessAccountDetails({ businessAccountDetails: response })),
          catchError(() => of(AuthActions.businessAccountDetails({ businessAccountDetails: businessAccount }))),
        );
      }),
    );
  });

  loadEnabledSolutions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.rehydrateState, AuthActions.refreshTokenSuccess),
      concatLatestFrom(() => [this.store.select(fromAuth.selectLoggedIn), this.store.select(fromAuth.selectBusinessAccount)]),
      filter(([, isLoggedIn, businessAccount]) => isLoggedIn && !!businessAccount),
      exhaustMap(() => {
        return this.transactionService.getEnabledSolutions().pipe(
          map((enabledSolutions) => AuthActions.loadEnabledSolutionsSuccess({ enabledSolutions })),
          catchError((error) =>
            of(AuthActions.loadEnabledSolutionsFailure({ error: error.message || 'Failed to load enabled solutions' })),
          ),
        );
      }),
    );
  });

  loginSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.businessAccountDetails),
      concatLatestFrom(() => [this.store.select(fromAuth.selectSponsorTenant)]),
      map(([, isSponsorTenant]) => {
        if (isSponsorTenant) {
          return AuthActions.showSplashScreen();
        }

        return LoginPageActions.loggedInRedirect();
      }),
    );
  });

  loggedInRedirect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(LoginPageActions.loggedInRedirect),
        concatLatestFrom(() => [this.store.select(fromRoot.selectQueryParams), this.store.select(fromAuth.selectTenant)]),
        tap({
          next: ([, queryParams, tenant]) => {
            if (isEmpty(this.authService.realmName) && tenant) {
              this.router.navigateByUrl(`${tenant}/app`, { skipLocationChange: false }).then(() => {
                this.pageReload();
              });
            } else {
              this.router.navigateByUrl(queryParams?.returnUrl || constants.HOME_ROUTE);
            }
          },
        }),
      );
    },
    { dispatch: false },
  );

  loggedInRedirectToMaintenance$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(LoginPageActions.loggedInRedirectToMaintenance),
        concatLatestFrom(() => [this.store.select(fromRoot.selectQueryParams), this.store.select(fromAuth.selectTenant)]),
        tap({
          next: ([payload, savedTenant]) => {
            const tenant = payload.tenant || savedTenant;
            if (isEmpty(this.authService.realmName) && tenant) {
              this.router.navigateByUrl(`${tenant}/maintenance`, { skipLocationChange: false }).then(() => {
                this.pageReload();
              });
            } else {
              this.router.navigate(['/maintenance']);
            }
          },
        }),
      );
    },
    { dispatch: false },
  );

  businessAccountSwitchRedirect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.businessAccountSwitchComplete),
        tap(() => {
          this.pageReload();
        }),
      );
    },
    { dispatch: false },
  );

  // A session that logged in while the linked business accounts lookup failed has no business account and
  // sends BA-scoped requests to `business-accounts/undefined`. Once the list loads, pick the business account
  // the login would have picked; selecting it refreshes the token and reloads the page.
  selectMissingBusinessAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.linkedBusinessAccountsListSuccess),
      concatLatestFrom(() => this.store.select(fromAuth.selectBusinessAccount)),
      filter(([, businessAccount]) => !businessAccount),
      map(([{ linkedBusinessAccounts }]) => find(linkedBusinessAccounts, ['parent', true]) ?? head(linkedBusinessAccounts)),
      filter((businessAccount): businessAccount is LinkedBusinessAccount => !!businessAccount),
      map((businessAccount) => AuthActions.selectBusinessAccount({ businessAccount })),
    );
  });

  businessAccountCheck$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      filter(({ businessAccount }) => !businessAccount),
      map(() => {
        return MessagesActions.displayWarning({ message: MESSAGE.NO_BUSINESS_ACCOUNT, title: '' });
      }),
    );
  });

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.logout),
      exhaustMap(({ params }) => {
        return this.authService.signOut().pipe(map(() => AuthActions.logoutCompleted({ params })));
      }),
    );
  });

  logoutCompleted$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.logoutCompleted),
      tap({
        next: ({ params }) => {
          const { skipNavigation, isReturnUrl } = params ?? {};
          const returnUrl = this.router.url;

          if (!skipNavigation) {
            const routerQueryParams =
              isReturnUrl && ![constants.HOME_ROUTE, '404', '/'].includes(returnUrl) ? { queryParams: { returnUrl } } : undefined;
            this.router.navigateByUrl(constants.AUTH_ROUTE, { replaceUrl: true, ...routerQueryParams });
          }

          this.storageService.setItem(constants.KOR_TRANSACTION_FILTER, {});
          this.storageService.setItem(constants.KOR_TRANSACTION_BATCH_FILTER, {});
          this.storageService.setItem(constants.KOR_TRANSACTION_BATCH_ACTIVITY_FILTER, {});
          this.storageService.setItem(constants.KOR_MOCKETBNK_LIST_FILTER, {});
          this.financialAccountService.setFilterParams({});
          this.financialAccountService.setListFilterParams({});
          this.earnedWageService.setFilterParams({});
          this.customerService.setBusinessFilterParams({});
          this.customerService.setIndividualFilterParams({});
          this.identityVerificationService.setIdvFilterParams({});
          this.businessAccountService.setBusinessAccountFilterParams({});
          this.mocketbnkService.setFilterParams(constants.KOR_MOCKETBNK_LIST_FILTER, {});
        },
      }),
      map(() => ModalsActions.dismissAll()),
    );
  });

  refreshToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.refreshToken, AuthActions.loginSuccess, AuthActions.rehydrateState),
      concatLatestFrom(() => [
        this.store.select(fromAuth.selectLoggedIn),
        this.store.select(fromAuth.selectTenant),
        this.store.select(fromAuth.selectBusinessAccount),
      ]),
      concatMap(([, isLoggedIn, tenant, businessAccount]) => {
        if (isLoggedIn && businessAccount?.id) {
          return this.authService.refreshToken(businessAccount.id, tenant!).pipe(
            map((tokenInfo) => {
              return AuthActions.refreshTokenSuccess({ tokenInfo });
            }),
            catchError(() => of(AuthActions.logout({ params: { isReturnUrl: true } }))),
          );
        } else {
          return of(AuthActions.noActiveSession());
        }
      }),
    );
  });

  refreshTokenSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.refreshTokenSuccess),
      concatLatestFrom(() => this.store.select(fromAuth.selectTokenExpireInMilliseconds)),
      delayWhen(([, expiresIn]) => timer(expiresIn * 0.9)),
      map(() => AuthActions.refreshToken()),
    );
  });

  selectBusinessAccountRefresh$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.selectBusinessAccount),
      concatLatestFrom(() => [this.store.select(fromAuth.selectLoggedIn), this.store.select(fromAuth.selectTenant)]),
      switchMap(([action, isLoggedIn, tenant]) => {
        const newBusinessAccount = action.businessAccount;
        if (isLoggedIn && newBusinessAccount?.id && tenant) {
          return this.authService.refreshToken(newBusinessAccount.id, tenant).pipe(
            switchMap((tokenInfo) =>
              this.businessAccountService.getBusinessAccount(newBusinessAccount.id).pipe(
                switchMap((businessAccountDetails) => [
                  AuthActions.refreshTokenSuccess({ tokenInfo }),
                  AuthActions.businessAccountDetails({ businessAccountDetails }),
                  AuthActions.businessAccountSwitchComplete(),
                ]),
                catchError(() => [
                  AuthActions.refreshTokenSuccess({ tokenInfo }),
                  AuthActions.businessAccountDetails({ businessAccountDetails: null }),
                  AuthActions.businessAccountSwitchComplete(),
                ]),
              ),
            ),
            catchError(() => of(AuthActions.logout({ params: { isReturnUrl: true } }))),
          );
        }
        return of();
      }),
    );
  });

  splashScreenAnimationEnd$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LoginPageActions.splashScreenAnimationEnd),
      debounceTime(500),
      map(() => LoginPageActions.loggedInRedirect()),
    );
  });

  getLinkedBusinessAccounts$ = createEffect(() => {
    const concurrent = 4;
    const pageSizeLimit = 50;
    return this.actions$.pipe(
      ofType(AuthActions.loginSuccess, AuthActions.linkedBusinessAccountsListLoad, AuthActions.rehydrateState),
      concatLatestFrom(() => this.store.select(fromAuth.selectLoggedIn)),
      filter(([, isLoggedIn]) => isLoggedIn),
      exhaustMap(() => {
        return this.businessAccountService
          .getLinkedBusinessAccounts({ size: pageSizeLimit, page: 0 })
          .pipe(
            switchMap((firstPage) => {
              if (firstPage.totalElements === 0 || firstPage.content.length === 0) {
                return of({ firstPage, linkedBusinessAccounts: [] });
              }

              const linkedBusinessAccountsPages = [...Array(Math.ceil(firstPage.totalElements / pageSizeLimit) - 1).keys()];

              return from(linkedBusinessAccountsPages)
                .pipe(
                  mergeMap(
                    (page) =>
                      from(
                        this.businessAccountService
                          .getLinkedBusinessAccounts({ size: pageSizeLimit, page: page + 1 })
                          .pipe(map((pageResponse) => [...pageResponse.content])),
                      ),
                    concurrent,
                  ),
                  toArray(),
                )
                .pipe(map((linkedBusinessAccounts) => ({ firstPage, linkedBusinessAccounts })));
            }),
            map(({ firstPage, linkedBusinessAccounts }) => flatten(linkedBusinessAccounts.concat(firstPage.content))),
          )
          .pipe(
            map((data) => {
              return AuthActions.linkedBusinessAccountsListSuccess({
                linkedBusinessAccounts: data,
              });
            }),
            catchError((error) => {
              return of(AuthActions.linkedBusinessAccountsListFailure({ error: error.message }));
            }),
          );
      }),
    );
  });
}
